import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ImageWithHover } from '@/components/ImageWithHover/ImageWithHover';
import { useItemOrPriceResultLink } from '@/hooks/useItemOrPriceResultLink';
import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryFirstImageUrl,
    getItemSummaryImageUrlByImageNumber,
    getItemSummaryPhotos,
    getItemSummarySEOExcluded,
    getItemSummaryShortDescription,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import noop from '@/utils/noop';
import { addImageParams, GetImageUrlParams, imagePlaceholder } from '@/utils/urls';

import { DEFAULT_CARD_DIMENSIONS } from '../GridItemCard/Card/constants/defaultCardDimensions.constant';
import { CardStatusBanner } from './CardStatusBanner';

type Props = {
    applyDimensionToUrl?: boolean;
    className?: string;
    /** Both the height and width of image */
    dimension?: number;
    imageQuality?: number;
    isCatalogPage?: boolean;
    itemId: number;
    lazyLoadImage?: boolean;
    noWidthLimit?: boolean;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onNavigate?: (itemUrl?: string) => void;
    sharpenImage?: boolean;
    showBidStatus?: boolean;
    showImageBorder?: boolean;
    showImageWithHover?: boolean;
};

const ImageRowComponent = ({
    applyDimensionToUrl,
    className,
    dimension,
    imageQuality,
    isCatalogPage,
    itemId,
    lazyLoadImage = true,
    noWidthLimit,
    onMouseEnter = noop,
    onMouseLeave = noop,
    onNavigate,
    sharpenImage = false,
    showBidStatus,
    showImageBorder,
    showImageWithHover = false,
}: Props) => {
    const photos = useAppSelector((state) => getItemSummaryPhotos(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const shortDescription = useAppSelector((state) => getItemSummaryShortDescription(state, itemId));
    const firstPhoto = useAppSelector((state) => getItemSummaryFirstImageUrl(state, itemId));
    const itemUrl = useItemOrPriceResultLink(itemId, true);
    const alt = shortDescription ? `${title}: ${shortDescription}` : title;

    // to ensure we only get a second photo if there is one, we check first to see if there is a photo
    const secondPhoto = useAppSelector((state) =>
        getItemSummaryImageUrlByImageNumber(state, photos?.length > 1 ? itemId : undefined, photos?.[1])
    );

    const imageOptions: GetImageUrlParams = {};
    const imageSizeProps: { height: number; width: number } = {
        height: dimension || DEFAULT_CARD_DIMENSIONS.desktop,
        width: dimension || DEFAULT_CARD_DIMENSIONS.desktop,
    };
    if (noWidthLimit) {
        imageOptions.height = 170;
    } else {
        imageOptions.height = 310;
    }

    if (applyDimensionToUrl) {
        imageOptions.width = dimension;
        imageOptions.height = dimension;
    }

    if (sharpenImage) {
        imageOptions.sharpen = true;
    }

    const imageUrl = addImageParams({ ...imageOptions, quality: imageQuality, url: firstPhoto });

    let secondaryImageUrl: string | undefined;

    if (secondPhoto) {
        secondaryImageUrl = addImageParams({
            ...imageOptions,
            url: secondPhoto,
        });
    }

    let cardImage = (
        <img
            alt={alt}
            className={cn(
                'absolute inset-0 m-auto block h-full max-h-full w-full max-w-full object-contain print:m-0',
                noWidthLimit && 'relative m-auto block max-h-[170px] w-auto max-w-[500px]'
            )}
            loading={lazyLoadImage ? 'lazy' : 'eager'}
            onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (target.src !== imagePlaceholder) {
                    target.src = imagePlaceholder;
                }
            }}
            src={imageUrl}
            {...imageSizeProps}
        />
    );

    if (Boolean(showImageWithHover) && Boolean(secondPhoto)) {
        cardImage = (
            <ImageWithHover
                alt={alt}
                lazyLoadImage={lazyLoadImage}
                srcPrimary={imageUrl}
                srcSecondary={secondaryImageUrl}
                {...imageSizeProps}
            />
        );
    }

    const navigate = useNavigate();
    const isSeoExcluded = useAppSelector((state) => getItemSummarySEOExcluded(state, itemId));

    return (
        <Link
            className={cn(
                'mx-auto block cursor-pointer text-center print:mr-xs print:max-h-[100px] print:w-[unset] print:max-w-[100px]',
                noWidthLimit && 'w-fit',
                isCatalogPage && 'max-[1200px]:w-[unset] mx-0',
                showImageBorder && 'border border-border-secondary',
                className
            )}
            data-testid="itemCard"
            href={itemUrl}
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onNavigate?.(itemUrl);
                navigate(itemUrl);
            }}
            rel={isSeoExcluded ? 'nofollow' : ''}
        >
            <div
                className={cn(
                    'relative box-border flex max-h-full w-full min-w-[126px] justify-center transition-all',
                    noWidthLimit ? 'h-[170px] w-auto' : 'pb-[100%]'
                )}
                data-testid="itemCardImage"
            >
                <div
                    className={cn(noWidthLimit && 'flex h-[170px] w-auto justify-center')}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                >
                    {cardImage}
                </div>
                {showBidStatus && <CardStatusBanner itemId={itemId} />}
            </div>
        </Link>
    );
};

export const ImageRow = memo<Props>(ImageRowComponent);
